import { createSelector } from 'reselect';

import {
  projectsByIdSelector,
  projectsListSelector,
  rawActiveProjectIdSelector
} from 'reducers/projects/selectors';
import {
  instancesByIdSelector,
  instancesByProjectIdSelector
} from 'reducers/instances/selectors';
import {
  creditsByIdSelector,
  creditsByInstanceIdSelector,
  creditsByProjectIdSelector
} from 'reducers/credits/selectors';
import {
  usersByIdSelector,
  usersByProjectIdSelector
} from 'reducers/users/selectors';
import {
  paymentMethodsByIdSelector,
  paymentMethodsByProjectIdSelector
} from 'reducers/paymentMethods/selectors';
import {
  skusByIdSelector,
  skusByProjectIdSelector
} from 'reducers/skus/selectors';
import { groupsSelector } from '../groups/groupsSelectors';

export const activeProjectCreditsSelector = createSelector(
  rawActiveProjectIdSelector,
  creditsByIdSelector,
  creditsByProjectIdSelector,
  instancesByIdSelector,
  groupsSelector,
  (activeProjectId, credits, creditsByProjectId, instances, groups) => {
    const projectCredits = creditsByProjectId.get(activeProjectId);

    return projectCredits
      ? projectCredits.map((creditId) => {
          const credit = credits.get(creditId);
          const groupId = credit.get('group_id');
          const instanceAlias =
            instances.getIn([`${credit.get('instance_id')}`, 'alias']) || null;

          const group = groups.find(({ id }) => id === groupId);

          return credit
            .set('instance_alias', instanceAlias)
            .set('group', group);
        })
      : [];
  }
);

export const activeProjectInstancesSelector = createSelector(
  rawActiveProjectIdSelector,
  instancesByIdSelector,
  instancesByProjectIdSelector,
  creditsByIdSelector,
  creditsByInstanceIdSelector,
  (
    activeProjectId,
    instances,
    instancesByProjectId,
    credits,
    creditsByInstanceId
  ) => {
    const projectInstances = instancesByProjectId.get(activeProjectId);
    return projectInstances
      ? projectInstances.map((instanceId) => {
          const instance = instances.get(instanceId);
          const instanceCreditIds = creditsByInstanceId.get(instanceId) || [];
          const instanceCredits = instanceCreditIds.map((creditId) =>
            credits.get(creditId)
          );

          return instance.set('credits', instanceCredits);
        })
      : [];
  }
);

export const activeProjectUsersSelector = createSelector(
  rawActiveProjectIdSelector,
  usersByIdSelector,
  usersByProjectIdSelector,
  (activeProjectId, users, usersByProjectId) => {
    const projectUsers = usersByProjectId.get(activeProjectId);
    return projectUsers ? projectUsers.map((userId) => users.get(userId)) : [];
  }
);

export const activeProjectPaymentMethodsSelector = createSelector(
  rawActiveProjectIdSelector,
  paymentMethodsByIdSelector,
  paymentMethodsByProjectIdSelector,
  (activeProjectId, paymentMethods, paymentMethodsByProjectId) => {
    const projectPaymentMethods =
      paymentMethodsByProjectId.get(activeProjectId);

    return projectPaymentMethods
      ? projectPaymentMethods.map((paymentMethodId) =>
          paymentMethods.get(paymentMethodId)
        )
      : [];
  }
);

export const activeProjectSkusSelector = createSelector(
  rawActiveProjectIdSelector,
  skusByIdSelector,
  skusByProjectIdSelector,
  (activeProjectId, skus, skusByProjectId) => {
    const projectSkus = skusByProjectId.get(activeProjectId);

    return projectSkus ? projectSkus.map((skuId) => skus.get(skuId)) : [];
  }
);

export const activeProjectSelector = createSelector(
  rawActiveProjectIdSelector,
  projectsByIdSelector,
  (activeProjectId, projectsById) => projectsById.get(activeProjectId) || {}
);

export const allProjectsSelector = createSelector(
  projectsListSelector,
  (allProjects) => allProjects.get() || {}
);
